import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "../assets/styles/SareesPage.css";
import { BaseUrl } from "../config/api";
import defaultSareeImage from "../assets/images/saree-category.png";
import { toHttps } from "../utils/imageUtils";

const SareesPage = () => {
    const [sareeCategories, setSareeCategories] = useState([]);
    const [otherCategoryTypes, setOtherCategoryTypes] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Fetch category types
                const categoryTypesResponse = await fetch(
                    `${BaseUrl}/categorytypes/allcategorytypes`
                );
                const categoryTypesData = categoryTypesResponse.ok
                    ? await categoryTypesResponse.json()
                    : [];

                // Fetch categories
                const categoryResponse = await fetch(
                    `${BaseUrl}/categories/allcategory`
                );
                const categoryData = categoryResponse.ok
                    ? await categoryResponse.json()
                    : [];

                // Fetch all products
                const productsResponse = await fetch(
                    `${BaseUrl}/products/getallproducts`
                );
                const productsData = productsResponse.ok
                    ? await productsResponse.json()
                    : [];

                if (
                    !Array.isArray(categoryData) ||
                    !Array.isArray(productsData) ||
                    !Array.isArray(categoryTypesData)
                ) {
                    console.error("Invalid data format received");
                    setLoading(false);
                    return;
                }

                // --- Saree categories ---
                const filteredSareeCategories = categoryData
                    .filter((category) => {
                        if (
                            category.categoryTypeName?.toLowerCase() !== "saree" ||
                            category.isDeleted
                        ) {
                            return false;
                        }
                        const availableProductsCount = productsData.filter(
                            (product) =>
                                !product.isDeleted &&
                                product.categoryName === category.categoryName &&
                                product.stock > 0 &&
                                !product.outOfStock
                        ).length;
                        return availableProductsCount > 0;
                    })
                    .map((category) => ({
                        name: category.categoryName,
                        image:
                            toHttps(category.categotyImage) ||
                            BaseUrl + category.categotyImage ||
                            defaultSareeImage,
                        path: category.categoryName.toLowerCase().replace(/\s+/g, "-"),
                    }));

                setSareeCategories(filteredSareeCategories);

                // --- Other (non-saree) categories ---
                const activeTypes = categoryTypesData.filter(
                    (type) =>
                        !type.isDeleted && type.typeName.toLowerCase() !== "saree"
                );

                const categoryMap = {};
                activeTypes.forEach((type) => {
                    const categoriesForType = categoryData
                        .filter((category) => {
                            if (
                                category.categoryTypeId !== type._id ||
                                category.isDeleted
                            ) {
                                return false;
                            }
                            const availableProductsCount = productsData.filter(
                                (product) =>
                                    !product.isDeleted &&
                                    product.categoryName === category.categoryName &&
                                    product.stock > 0 &&
                                    !product.outOfStock
                            ).length;
                            return availableProductsCount > 0;
                        })
                        .map((category) => ({
                            name: category.categoryName,
                            image: toHttps(category.categotyImage) || defaultSareeImage,
                            path: category.categoryName.toLowerCase().replace(/\s+/g, "-"),
                        }));

                    if (categoriesForType.length > 0) {
                        categoryMap[type._id] = {
                            typeName: type.typeName,
                            categories: categoriesForType,
                        };
                    }
                });

                setOtherCategoryTypes(categoryMap);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching saree page data:", error);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    return (
        <div className="sarees-page">
            {/* Hero Banner */}
            <div className="sarees-hero-banner">
                <img
                    src="https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=1600&q=80"
                    alt="Sarees Collection Banner"
                    onError={(e) => {
                        e.target.style.display = "none";
                    }}
                />
                <div className="sarees-hero-overlay">
                    <h1>Sarees</h1>
                </div>
            </div>

            {/* Loading State */}
            {loading && <div className="sarees-loading">Loading categories...</div>}

            {/* Saree Categories - no title, just cards */}
            {!loading && sareeCategories.length > 0 && (
                <div className="sarees-categories-section">
                    <div className="sarees-category-cards">
                        {sareeCategories.map((category, index) => (
                            <Link
                                to={`/category/${category.path}`}
                                key={index}
                                className="saree-card-link"
                            >
                                <div className="saree-card">
                                    <img
                                        src={category.image}
                                        alt={category.name}
                                        onError={(e) => {
                                            e.target.src = defaultSareeImage;
                                        }}
                                    />
                                    <div className="saree-card-overlay">
                                        <h3>{category.name}</h3>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Other Category Types */}
            {!loading &&
                Object.keys(otherCategoryTypes).length > 0 &&
                Object.keys(otherCategoryTypes).map((typeId) => (
                    <div className="sarees-categories-section" key={typeId}>
                        <h2 className="sarees-section-title">
                            {otherCategoryTypes[typeId].typeName}
                        </h2>
                        <div className="sarees-category-cards">
                            {otherCategoryTypes[typeId].categories.map(
                                (category, index) => (
                                    <Link
                                        to={`/category/${category.path}`}
                                        key={index}
                                        className="saree-card-link"
                                    >
                                        <div className="saree-card">
                                            <img
                                                src={category.image}
                                                alt={category.name}
                                                onError={(e) => {
                                                    e.target.src = defaultSareeImage;
                                                }}
                                            />
                                            <div className="saree-card-overlay">
                                                <h3>{category.name}</h3>
                                            </div>
                                        </div>
                                    </Link>
                                )
                            )}
                        </div>
                    </div>
                ))}

            {/* Empty State */}
            {!loading &&
                sareeCategories.length === 0 &&
                Object.keys(otherCategoryTypes).length === 0 && (
                    <div className="sarees-empty">
                        No categories available at the moment. Please check back later.
                    </div>
                )}
        </div>
    );
};

export default SareesPage;
